/**
 * @graphql cc
 */
import type React from 'react';
import { useEffect, useState, useRef, useCallback } from 'react';
import gql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';

import { abortAll } from '@atlaskit/react-ufo/interaction-metrics';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop';
import { CommentType } from '@confluence/comments-data/entry-points/useCommentsData';
import { fg } from '@confluence/feature-gating';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { useCommentById } from '@confluence/comments-data-manager/entry-points/useCommentById';
import { buildDefaultCommentsContentStatusFilters } from '@confluence/comments-data-manager/entry-points/useAllCommentsData';
import {
	handleFooterCommentsNavigation,
	ScrollBehavior,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils';
import { useShowCommentsPanel } from '@confluence/comments-panel/entry-points/useShowCommentsPanel';
import { LocationValue } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper';
import {
	ViewValues,
	useCommentsPanelActions,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import { getLogger } from '@confluence/logger';
import { usePageContentId } from '@confluence/page-context';
import { createSingleQueryParamHook } from '@confluence/route-manager/entry-points/RouteState';

import type {
	ObjectCommentQueryParamsListenerObjectCommentQueryParamsQuery as ObjectCommentQueryParamsListenerObjectCommentQueryParamsQuery$data,
	ObjectCommentQueryParamsListenerObjectCommentQueryParamsQueryVariables as ObjectCommentQueryParamsListenerObjectCommentQueryParamsQuery$variables,
	ObjectCommentQueryParamsListenerObjectCommentQueryParamsQuery_comments_PaginatedCommentList_nodes_Comment_location_FooterComment as FooterCommentLocation,
} from './__types__/ObjectCommentQueryParamsListenerObjectCommentQueryParamsQuery';

const ObjectCommentQueryParamsListenerObjectCommentQueryParamsQuery = gql`
	query ObjectCommentQueryParamsListenerObjectCommentQueryParamsQuery($commentId: ID!) {
		comments(commentId: $commentId) {
			nodes {
				id
				location {
					type
					... on FooterComment {
						commentResolveProperties {
							resolved
						}
					}
				}
			}
		}
	}
`;

const logger = getLogger('inline-comments:query-param-listener');

const useFocusedCommentIdQueryParam = createSingleQueryParamHook('focusedCommentId');
const useReplyToCommentQueryParam = createSingleQueryParamHook('replyToComment');

// Maximum wait time for scroll operations (in milliseconds)
const SCROLL_TIMEOUT_MS = 4000;

// Minimum time page must be stable before scrolling (in milliseconds)
const PAGE_STABILITY_DELAY_MS = 500;

// If there is a link to non-existent (probably deleted) comment, we don't want to show an error
const expectedErrorCodes = [404];

const isExpectedError = (error: ApolloErrorInterop) =>
	error.graphQLErrors.some((e) => expectedErrorCodes.includes(e?.extensions?.statusCode));

type ObjectCommentQueryParamsListenerProps = {
	hasLoadedObjectComments: boolean;
	allObjectCommentsRendered: boolean;
	commentBatchRendered?: boolean;
	commentBatchCount?: number;
	setForceLoadComments: (value: React.SetStateAction<boolean>) => void;
};

export const ObjectCommentQueryParamsListener: React.FC<ObjectCommentQueryParamsListenerProps> = ({
	hasLoadedObjectComments,
	allObjectCommentsRendered,
	commentBatchRendered,
	commentBatchCount,
	setForceLoadComments,
}) => {
	const [hasScrolledToFocusedComment, setHasScrolledToFocusedComment] = useState<boolean>(false);
	const [hasScrolledToReplyComment, setHasScrolledToReplyComment] = useState<boolean>(false);
	const mutationObserverRef = useRef<MutationObserver | null>(null);
	const scrollTimeoutRef = useRef<NodeJS.Timeout | number | null>(null);
	const lastMutationTimeRef = useRef<number>(0);

	// Helper function to clear scroll timeout/callback
	const clearScrollTimeout = () => {
		if (scrollTimeoutRef.current !== null) {
			if (typeof scrollTimeoutRef.current === 'number') {
				// requestIdleCallback ID
				if (
					typeof window !== 'undefined' &&
					'cancelIdleCallback' in window &&
					typeof (window as any).cancelIdleCallback === 'function'
				) {
					(window as any).cancelIdleCallback(scrollTimeoutRef.current);
				}
			} else {
				// setTimeout ID
				clearTimeout(scrollTimeoutRef.current);
			}
			scrollTimeoutRef.current = null;
		}
	};

	// Helper function to clean up MutationObserver
	const cleanupMutationObserver = () => {
		if (mutationObserverRef.current) {
			mutationObserverRef.current.disconnect();
			mutationObserverRef.current = null;
		}
	};

	// Helper function to clean up both observer and timeout (only if they were set up)
	const cleanupScrollResources = useCallback(() => {
		if (mutationObserverRef.current) {
			cleanupMutationObserver();
		}
		if (scrollTimeoutRef.current !== null) {
			clearScrollTimeout();
		}
	}, []); // Empty deps: only uses refs which don't change

	const { setCurrentlySelectedThreadKey, setReplyToCommentId } = useCommentsPanelActions();
	const { showCommentsPanel } = useShowCommentsPanel();
	const [contentId] = usePageContentId();

	const focusedCommentId = useFocusedCommentIdQueryParam();
	const replyToComment = useReplyToCommentQueryParam();

	const commentId = focusedCommentId || replyToComment;

	// NEW FLOW: Use useCommentById when experiment is enabled
	const { comment, error: commentError } = useCommentById({
		commentId: commentId || '',
		contentId: contentId || '',
		contentStatus: buildDefaultCommentsContentStatusFilters(),
		options: {
			skip: !commentId || !expVal('cc_comments_new_comments_data_manager', 'isEnabled', false),
		},
	});

	// OLD FLOW: Direct GraphQL query (fallback when experiment is disabled)
	const { data, error } = useQuery<
		ObjectCommentQueryParamsListenerObjectCommentQueryParamsQuery$data,
		ObjectCommentQueryParamsListenerObjectCommentQueryParamsQuery$variables
	>(ObjectCommentQueryParamsListenerObjectCommentQueryParamsQuery, {
		variables: {
			commentId: commentId!,
		},
		skip: !commentId || expVal('cc_comments_new_comments_data_manager', 'isEnabled', false),
	});
	if (error && !isExpectedError(error)) {
		logger.error`An error occurred when querying the focused object comment status; ${error}`;
	}

	let isResolved: boolean | undefined = undefined;
	// Extract resolved status from either new or old flow
	if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
		// NEW FLOW: Extract from useCommentById
		if (comment && !commentError) {
			const location = comment.location as FooterCommentLocation;
			isResolved = Boolean(location?.commentResolveProperties?.resolved);
		}
	} else {
		// OLD FLOW: Extract from GraphQL query
		// If the commentId is not present, error is defined and isResolved will be undefined
		if (data && !error) {
			const location = data?.comments?.nodes?.[0]?.location as FooterCommentLocation;
			isResolved = Boolean(location?.commentResolveProperties?.resolved);
		}
	}

	// Handle scrolling to focused comment or replyTo comment after the data is loaded
	useEffect(() => {
		// Determine if we have data from new or old flow
		const hasData = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
			? comment && !commentError
			: data && !error;

		if (
			// Note that if the commentId is not found, the query will return an error and we don't
			// have to scroll to the comment
			commentId &&
			hasData &&
			hasLoadedObjectComments &&
			allObjectCommentsRendered &&
			!hasScrolledToFocusedComment &&
			!hasScrolledToReplyComment
		) {
			if (isResolved) {
				showCommentsPanel({ openWithView: ViewValues.RESOLVED });
				setCurrentlySelectedThreadKey(commentId, CommentType.GENERAL);
				if (focusedCommentId) {
					setHasScrolledToFocusedComment(true);
				} else {
					setHasScrolledToReplyComment(true);
				}
			} else {
				const commentElementId = `footer-comment-thread-${focusedCommentId || replyToComment}-container`;
				const replyCommentElementId = `footer-comment-thread-${focusedCommentId || replyToComment}-replies`;

				const commentElement = document.getElementById(commentElementId);
				const replyCommentElement = document.getElementById(replyCommentElementId);
				const element = commentElement || replyCommentElement;

				// Use unified navigation if feature gate is enabled, otherwise use direct scrolling
				if (commentId && fg('confluence_comments_unified_navigation')) {
					if (expValEquals('confluence_comment_deeplink_delay_scroll', 'isEnabled', true)) {
						// Use MutationObserver to watch for when the element is ready
						// Clean up any existing observer first
						cleanupMutationObserver();

						// Perform scroll and cleanup observer
						const performScrollAndCleanup = () => {
							clearScrollTimeout();
							handleFooterCommentsNavigation({
								scrollBehavior: ScrollBehavior.SMOOTH,
								block: 'center',
								inline: 'center',
								commentId,
							});
							setHasScrolledToFocusedComment(true);
							setCurrentlySelectedThreadKey(focusedCommentId);
							cleanupMutationObserver();
						};

						// Schedule scroll when page is idle/stable
						const scheduleScroll = () => {
							clearScrollTimeout();

							// Track when we detected the element is ready
							lastMutationTimeRef.current = Date.now();

							// Use requestIdleCallback if available, otherwise fallback to setTimeout
							if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
								scrollTimeoutRef.current = (window as any).requestIdleCallback(() => {
									// Check if page has been stable (no mutations) for at least PAGE_STABILITY_DELAY_MS
									const timeSinceLastMutation = Date.now() - lastMutationTimeRef.current;
									if (timeSinceLastMutation >= PAGE_STABILITY_DELAY_MS) {
										// Page is stable, scroll
										performScrollAndCleanup();
									} else {
										// If not stable yet, wait a bit more
										scrollTimeoutRef.current = setTimeout(() => {
											performScrollAndCleanup();
										}, PAGE_STABILITY_DELAY_MS - timeSinceLastMutation);
									}
								});
							} else {
								// Fallback for browsers without requestIdleCallback (e.g., Safari)
								scrollTimeoutRef.current = setTimeout(() => {
									performScrollAndCleanup();
								}, SCROLL_TIMEOUT_MS);
							}
						};

						// Check if element exists and is ready (has content and is visible)
						const getElementAndCheckReady = () => {
							const foundElement =
								document.getElementById(commentElementId) ||
								document.getElementById(replyCommentElementId);
							if (!foundElement) {
								return { element: null, isReady: false };
							}
							const rect = foundElement.getBoundingClientRect();
							const elementIsReady =
								foundElement.offsetHeight > 0 &&
								foundElement.offsetWidth > 0 &&
								rect.height > 0 &&
								rect.width > 0;
							return { element: foundElement, isReady: elementIsReady };
						};

						const { element: currentElement, isReady } = getElementAndCheckReady();

						// If element exists and is already ready, schedule scroll with delay
						if (currentElement && isReady) {
							scheduleScroll();
						} else {
							// Set up MutationObserver to watch for when element appears or becomes ready
							mutationObserverRef.current = new MutationObserver(() => {
								// Update last mutation time when DOM changes
								lastMutationTimeRef.current = Date.now();
								const { element: foundElement, isReady: foundIsReady } = getElementAndCheckReady();
								if (foundElement && foundIsReady) {
									scheduleScroll();
								}
							});

							// If element exists, observe it and its parent
							if (currentElement) {
								mutationObserverRef.current.observe(currentElement, {
									childList: true,
									subtree: true,
									attributes: true,
									attributeFilter: ['style', 'class'],
								});

								const parentContainer = currentElement.parentElement;
								if (parentContainer) {
									mutationObserverRef.current.observe(parentContainer, {
										childList: true,
										subtree: true,
									});
								}
							} else {
								// If element doesn't exist yet, observe document.body to detect when it appears
								mutationObserverRef.current.observe(document.body, {
									childList: true,
									subtree: true,
								});
							}

							// Fallback timeout in case MutationObserver doesn't detect changes
							// This will also wait SCROLL_TIMEOUT_MS for page to stabilize
							setTimeout(() => {
								if (mutationObserverRef.current) {
									performScrollAndCleanup();
								}
							}, SCROLL_TIMEOUT_MS);
						}
					} else {
						// Experiment disabled - scroll immediately if element exists
						if (element) {
							handleFooterCommentsNavigation({
								scrollBehavior: ScrollBehavior.SMOOTH,
								block: 'center',
								inline: 'center',
								commentId,
							});
							setHasScrolledToFocusedComment(true);
							setCurrentlySelectedThreadKey(focusedCommentId);
						}
					}
				} else {
					// Unified navigation disabled - use direct scrolling if element exists
					if (element) {
						if (replyToComment) {
							setCurrentlySelectedThreadKey(replyToComment);
							setReplyToCommentId({
								parentCommentId: replyToComment,
								location: LocationValue.FOOTER,
							});
							setHasScrolledToReplyComment(true);
							return;
						}
						const rect = element.getBoundingClientRect();
						const commentIsInViewport =
							rect.top >= 0 &&
							rect.left >= 0 &&
							rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
							rect.right <= (window.innerWidth || document.documentElement.clientWidth);

						if (!commentIsInViewport) {
							// If the document scrolls to a focused comment on View or Live Page, the VC event is aborted.
							abortAll('new_interaction', `document-scroll:object-comment-container`);

							element.scrollIntoView({
								behavior: ScrollBehavior.SMOOTH,
								block: 'center',
								inline: 'center',
							});
							setHasScrolledToFocusedComment(true);
							setCurrentlySelectedThreadKey(focusedCommentId);
						}
					}
				}
			}
		}
	}, [
		data,
		error,
		comment,
		commentError,
		hasLoadedObjectComments,
		commentId,
		focusedCommentId,
		replyToComment,
		allObjectCommentsRendered,
		commentBatchRendered,
		commentBatchCount,
		setCurrentlySelectedThreadKey,
		setReplyToCommentId,
		isResolved,
		showCommentsPanel,
		hasScrolledToFocusedComment,
		hasScrolledToReplyComment,
	]);

	// Reset scroll tracking when focusedCommentId or replyToComment changes
	useEffect(() => {
		setHasScrolledToFocusedComment(false);
		setHasScrolledToReplyComment(false);
		// Clean up scroll resources when comment ID changes (only if they were set up)
		cleanupScrollResources();
	}, [focusedCommentId, replyToComment, cleanupScrollResources]);

	// Clean up MutationObserver and timeout on unmount
	useEffect(() => {
		return () => {
			cleanupScrollResources();
		};
	}, [cleanupScrollResources]);

	// Force load comments if focusedCommentId or replayToComment is present
	useEffect(() => {
		if ((focusedCommentId || replyToComment) && !hasLoadedObjectComments) {
			setForceLoadComments(true);
		}
	}, [focusedCommentId, replyToComment, hasLoadedObjectComments, setForceLoadComments]);

	return null;
};
